import { Button } from './Button';

// Compute the page numbers to show in the numbered pager.
// Always shows first + last; shows up to 3 pages around current; uses ellipsis gaps.
// Ported from apps/admin-portal/src/pages/accounts/AccountList.tsx `pageNumbers()` (#13136).
function pageNumbers(current: number, total: number): (number | '…')[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages: (number | '…')[] = [];
  const seen = new Set<number>();
  const addPage = (n: number) => {
    if (!seen.has(n)) { seen.add(n); pages.push(n); }
  };
  addPage(1);
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
    const last = pages[pages.length - 1];
    if (typeof last === 'number' && last !== i - 1) pages.push('…');
    addPage(i);
  }
  const last = pages[pages.length - 1];
  if (typeof last === 'number' && last !== total - 1) pages.push('…');
  addPage(total);
  return pages;
}

interface PaginationProps {
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  /** Omit to hide the page-size selector (e.g. when the caller manages page size elsewhere). */
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
  className?: string;
}

/**
 * Numbered pager with ellipsis + range label + optional page-size selector (#13136).
 * Renders nothing when there is no data at all. The range label always shows once there
 * is data; the numbered nav only shows once there is more than one page.
 */
export function Pagination({
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  className = '',
}: PaginationProps) {
  if (totalCount === 0) return null;

  const totalPages = Math.ceil(totalCount / pageSize);
  const rangeLabel = `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, totalCount)} of ${totalCount}`;

  return (
    <div className={`flex items-center justify-between px-4 py-3 flex-wrap gap-2 ${className}`}>
      <div className="flex items-center gap-3">
        <p className="text-sm text-gray-500" aria-live="polite">{rangeLabel}</p>
        {onPageSizeChange && (
          <select
            value={pageSize}
            onChange={e => onPageSizeChange(Number(e.target.value))}
            aria-label="Items per page"
            className="h-8 rounded-md border border-gray-300 px-2 text-sm"
          >
            {pageSizeOptions.map(size => (
              <option key={size} value={size}>{size} / page</option>
            ))}
          </select>
        )}
      </div>

      {totalPages > 1 && (
        <nav aria-label="Pagination" className="flex items-center gap-1">
          <Button
            variant="secondary"
            size="sm"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            aria-label="Previous page"
          >
            ‹
          </Button>
          {pageNumbers(page, totalPages).map((n, i) =>
            n === '…'
              ? <span key={`ellipsis-${i}`} className="px-2 text-gray-400 text-sm select-none" aria-hidden="true">…</span>
              : (
                <button
                  key={n}
                  type="button"
                  onClick={() => onPageChange(n)}
                  aria-label={`Page ${n}`}
                  aria-current={n === page ? 'page' : undefined}
                  className={`w-8 h-8 rounded text-sm font-medium transition-colors ${
                    n === page
                      ? 'bg-indigo-600 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {n}
                </button>
              ),
          )}
          <Button
            variant="secondary"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
            aria-label="Next page"
          >
            ›
          </Button>
        </nav>
      )}
    </div>
  );
}
